import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import BasicModal from "@/Modals/BasicModal";
import RenderToast from "@/components/RenderToast";

import {
  contactSupportsApi,
  adminListDataApiEndPoint,
  contactSupportReasonsApi,
  webUsersApi,
} from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";

import BasicInput from "@/components/BasicInput";
import AdminContactSupportValues from "@/data/CommunicationMGMT/ContactSupport/admin-intial-values";
import AdminContactSupportSchema from "@/schema/CommunicationMGMT/ContactSupport/admin-schema";

const NewTicketModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const showApi = modalState.id
    ? `${contactSupportsApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = () => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `The Ticket has been created successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${contactSupportsApi}`,
    isFormData: true,
    onSuccess: handleSuccess(),
  });

  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending],
    [saveEditError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: AdminContactSupportValues(modalState.id ? showData : null),
    validationSchema: AdminContactSupportSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const adminListApi = `${adminListDataApiEndPoint}${baseQueryParams}`;
  const defaultAdminLabelFormatter = useCallback((item) => item.first_name, []);
  const {
    loading: adminLoading,
    defaultOptions: adminDefaultOptions,
    debouncedFetchOptions: adminFetchOptions,
    getSelectedOption: adminGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: adminListApi,
    formikValue: formik.values.assigned_to_id,
    formikInitialValue: formik.initialValues.assigned_to_id,
    showData: !!modalState?.id && showData?.data?.assigned_to,
    labelFormatter: defaultAdminLabelFormatter,
  });

  const reasonListApi = `${contactSupportReasonsApi}`;
  const defaultReasonLabelFormatter = useCallback((item) => item.name, []);
  const {
    loading: reasonLoading,
    defaultOptions: reasonDefaultOptions,
    debouncedFetchOptions: reasonFetchOptions,
    getSelectedOption: reasonGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: reasonListApi,
    formikValue: formik.values.contact_support_reason_id,
    formikInitialValue: formik.initialValues.contact_support_reason_id,
    showData: !!modalState?.id && showData?.data?.contact_support_reason,
    labelFormatter: defaultReasonLabelFormatter,
  });

  const defaultUserLabelFormatter = useCallback((item) => item.username, []);

  const webUserListApi = `${webUsersApi}${baseQueryParams}`;
  const {
    loading: webUserLoading1,
    defaultOptions: webUserDefaultOptions1,
    debouncedFetchOptions: webUserFetchOptions1,
    getSelectedOption: webUserGetSelectedOption1,
  } = useModalGetOptions({
    modalState,
    listApi: webUserListApi,
    formikValue: formik.values.participant_id_1,
    formikInitialValue: formik.initialValues.participant_id_1,
    showData: !!modalState?.id && showData?.data?.participant_1,
    labelFormatter: defaultUserLabelFormatter,
  });

  const {
    loading: webUserLoading2,
    defaultOptions: webUserDefaultOptions2,
    debouncedFetchOptions: webUserFetchOptions2,
    getSelectedOption: webUserGetSelectedOption2,
  } = useModalGetOptions({
    modalState,
    listApi: webUserListApi,
    formikValue: formik.values.participant_id_2,
    formikInitialValue: formik.initialValues.participant_id_2,
    showData: !!modalState?.id && showData?.data?.participant_2,
    labelFormatter: defaultUserLabelFormatter,
  });

  const participantOptions = [
    {
      label: "Participant 1",
      required: true,
      fieldName: "participant_id_1",
      defaultValue: formik.values.participant_id_1,
      value: webUserGetSelectedOption1(),
      showData: !!modalState?.id && showData?.data?.participant1,
      options: webUserDefaultOptions1,
      loadOptions: webUserFetchOptions1,
      isLoading: webUserLoading1,
    },
    {
      label: "Participant 2",
      fieldName: "participant_id_2",
      required: false,
      defaultValue: formik.values.participant_id_2,
      value: webUserGetSelectedOption2(),
      showData: !!modalState?.id && showData?.data?.participant2,
      options: webUserDefaultOptions2,
      loadOptions: webUserFetchOptions2,
      isLoading: webUserLoading2,
    },
  ];

  const participantInputs = participantOptions.map((option) => (
    <AsyncDropdown
      key={option.fieldName}
      required={option.required}
      name={option.fieldName}
      className="mb-4"
      placeholder="No participant selected. Type to search..."
      label={option.label}
      defaultOptions={option.options}
      isLoading={option.isLoading}
      loadOptions={option.loadOptions}
      value={option.value}
      onChange={(selected) =>
        formik.setFieldValue(
          option.fieldName,
          selected?.value || formik.values[option.fieldName] || null
        )
      }
      errorText={
        (formik.touched[option.fieldName] && formik.errors[option.fieldName]) ||
        (saveError?.status === 422 &&
          saveError?.response?.data?.errors?.[option.fieldName]?.join(" "))
      }
    />
  ));

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) {
      formData.append("_method", "PUT");
    }
    formData.append("assigned_to_id", values.assigned_to_id);
    formData.append(
      "contact_support_reason_id",
      values.contact_support_reason_id
    );
    formData.append("participant_id_1", values.participant_id_1);
    formData.append("participant_id_2", values.participant_id_2);
    formData.append("opening_message", values.opening_message);
    formData.append(
      "reference_transaction_id",
      values.reference_transaction_id
    );
    formData.append("reference_username", values.reference_username);

    saveEdit(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={"New Ticket"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"reference_transaction_id"}
            saveError={saveError}
            label="Reference Transaction-ID"
            required={false}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"reference_username"}
            saveError={saveError}
            label="Reference Username"
            required={false}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Reason"
            placeholder="No reason selected. Type to search..."
            defaultOptions={reasonDefaultOptions}
            loadOptions={reasonFetchOptions}
            isLoading={reasonLoading}
            value={reasonGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "contact_support_reason_id",
                selected?.value || null
              );
            }}
            errorText={
              (formik.touched.contact_support_reason_id &&
                formik.errors.contact_support_reason_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Assign To"
            placeholder="No admin selected. Type to search..."
            defaultOptions={adminDefaultOptions}
            loadOptions={adminFetchOptions}
            isLoading={adminLoading}
            value={adminGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("assigned_to_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.assigned_to_id && formik.errors.assigned_to_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>
      {participantInputs}

      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"opening_message"}
            saveError={saveError}
            label="Opening Message"
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default NewTicketModal;
